/**
 * VSLeads Service - Serviço para gerenciamento de captura de leads
 *
 * Fornece métodos para buscar e gerenciar vídeos com captura de leads
 */

import { api } from './api';

export interface VSLeadFormConfig {
  // Campos de ativação
  haveForm: boolean;
  typeForm: string;

  // Campos de configuração do formulário - Inputs
  haveInputEmail: boolean;
  haveInputName: boolean;
  haveInputWhatsapp: boolean;
  skipLead: boolean;

  // Campos de configuração do formulário - Textos
  captureTitle: string;
  captureButtonText: string;
  captureAuxText: string;
  haveCaptureText: boolean;
  haveCaptureAuxText: boolean;
  haveCaptureBigTitle: boolean;

  // Campos de configuração do formulário - Cores
  haveBackgroundColored: boolean;
  captureBackgroundColor: string;
  captureTitleColor: string;
  captureButtonColor: string;
  captureButtonTextColor: string;
  captureAuxTextColor: string;
  captureInputColor: string;

  // Campos de configuração do formulário - Layout e Timer
  checkedCaptureVertical: boolean;
  checkedCaptureTimer: boolean;
  haveTime: number;

  // Senha (para capture-password)
  senha: string;
}

export interface VSLeadVideoSimplified {
  id: string;
  videoId: string;
  videoTitle: string;
  videoThumbnail: string | null;
  tipo: 'captura-dados' | 'captura-senha';
  createdAt: Date;
  status: 'active' | 'inactive';
  totalViews?: number;
  formConfig: VSLeadFormConfig;
}

// Linha de lead do relatório (endpoint /capture/leads) — já enriquecida pelo
// backend com o nome do vídeo e o tipo de captura.
export interface LeadReportRow {
  id: string;
  id_video: string;
  nome: string;
  email: string;
  telefone: string;
  createdAt: string;
  video_name: string;
  type: 'data_capture' | 'password_capture';
}

export interface LeadsReportParams {
  page: number;
  pageSize: number;
  videoId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  sortField?: 'created_at' | 'name' | 'email' | 'video_name';
  sortDir?: 'asc' | 'desc';
}

export interface LeadsReportResult {
  data: LeadReportRow[];
  total: number;
  page: number;
  pageSize: number;
}

export const vsleadsService = {
  /**
   * GET /videos/get/:id_user
   * Busca vídeos do usuário e filtra apenas os que têm captura de leads ativa
   */
  getVideosWithLeadCapture: async (userId: string): Promise<VSLeadVideoSimplified[]> => {
    try {
      const response = await api.get(`/videos/get/${userId}`);

      // Extrair array de vídeos da resposta
      let videosArray: any[] = [];
      if (response?.response && Array.isArray(response.response)) {
        videosArray = response.response;
      } else if (Array.isArray(response)) {
        videosArray = response;
      } else if (response?.data && Array.isArray(response.data)) {
        videosArray = response.data;
      }

      // Filtrar apenas vídeos que têm captura de leads ativa (haveForm = true)
      const videosWithLeadCapture = videosArray
        .filter((video: any) => video.haveForm === true)
        .map((video: any) => {
          // Determinar o tipo de captura
          const tipo: 'captura-dados' | 'captura-senha' =
            video.typeForm === 'capture-password' ? 'captura-senha' : 'captura-dados';

          const formConfig: VSLeadFormConfig = {
            haveForm: video.haveForm || false,
            typeForm: video.typeForm || '',
            haveInputEmail: video.haveInputEmail || false,
            haveInputName: video.haveInputName || false,
            haveInputWhatsapp: video.haveInputWhatsapp || false,
            skipLead: video.skipLead || false,
            captureTitle: video.captureTitle || '',
            captureButtonText: video.captureButtonText || '',
            captureAuxText: video.captureAuxText || '',
            haveCaptureText: video.haveCaptureText || false,
            haveCaptureAuxText: video.haveCaptureAuxText || false,
            haveCaptureBigTitle: video.haveCaptureBigTitle || false,
            haveBackgroundColored: video.haveBackgroundColored || false,
            captureBackgroundColor: video.captureBackgroundColor || '#FFFFFF',
            captureTitleColor: video.captureTitleColor || '#000000',
            captureButtonColor: video.captureButtonColor || '#3B82F6',
            captureButtonTextColor: video.captureButtonTextColor || '#FFFFFF',
            captureAuxTextColor: video.captureAuxTextColor || '#6B7280',
            captureInputColor: video.captureInputColor || '#F3F4F6',
            checkedCaptureVertical: video.checkedCaptureVertical || false,
            checkedCaptureTimer: video.checkedCaptureTimer || false,
            haveTime: video.haveTime || 0,
            senha: video.senha || '',
          };

          return {
            id: video.id_video,
            videoId: video.id_video,
            videoTitle: video.nomeVideo || 'Sem título',
            videoThumbnail: video.frame || video.thumb || null,
            tipo,
            createdAt: new Date(video.createdAt || Date.now()),
            status: 'active' as const,
            totalViews: 0,
            formConfig,
          };
        });

      return videosWithLeadCapture;
    } catch (error) {
      console.error('VSLeads Service: Erro ao buscar vídeos:', error);
      throw error;
    }
  },

  /**
   * GET /videos/get-by-id/:id_video
   * Busca um vídeo específico por ID
   */
  getVideoById: async (videoId: string): Promise<VSLeadVideoSimplified | null> => {
    try {
      const response = await api.get(`/videos/get-by-id/${videoId}`);

      if (!response || !response.haveForm) {
        return null;
      }

      const video = response;
      const tipo: 'captura-dados' | 'captura-senha' =
        video.typeForm === 'capture-password' ? 'captura-senha' : 'captura-dados';

      const formConfig: VSLeadFormConfig = {
        haveForm: video.haveForm || false,
        typeForm: video.typeForm || '',
        haveInputEmail: video.haveInputEmail || false,
        haveInputName: video.haveInputName || false,
        haveInputWhatsapp: video.haveInputWhatsapp || false,
        skipLead: video.skipLead || false,
        captureTitle: video.captureTitle || '',
        captureButtonText: video.captureButtonText || '',
        captureAuxText: video.captureAuxText || '',
        haveCaptureText: video.haveCaptureText || false,
        haveCaptureAuxText: video.haveCaptureAuxText || false,
        haveCaptureBigTitle: video.haveCaptureBigTitle || false,
        haveBackgroundColored: video.haveBackgroundColored || false,
        captureBackgroundColor: video.captureBackgroundColor || '#FFFFFF',
        captureTitleColor: video.captureTitleColor || '#000000',
        captureButtonColor: video.captureButtonColor || '#3B82F6',
        captureButtonTextColor: video.captureButtonTextColor || '#FFFFFF',
        captureAuxTextColor: video.captureAuxTextColor || '#6B7280',
        captureInputColor: video.captureInputColor || '#F3F4F6',
        checkedCaptureVertical: video.checkedCaptureVertical || false,
        checkedCaptureTimer: video.checkedCaptureTimer || false,
        haveTime: video.haveTime || 0,
        senha: video.senha || '',
      };

      return {
        id: video.id_video,
        videoId: video.id_video,
        videoTitle: video.nomeVideo || 'Sem título',
        videoThumbnail: video.frame || video.thumb || null,
        tipo,
        createdAt: new Date(video.createdAt || Date.now()),
        status: 'active',
        totalViews: 0,
        formConfig,
      };
    } catch (error) {
      console.error('VSLeads Service: Erro ao buscar vídeo por ID:', error);
      return null;
    }
  },

  /**
   * PATCH /data-capture/:id_video
   * Atualiza as configurações de captura de dados (email) de um vídeo
   */
  updateDataCapture: async (videoId: string, data: Partial<VSLeadFormConfig>): Promise<any> => {
    try {
      const response = await api.patch(`/videos/data-capture/${videoId}`, data);
      return response;
    } catch (error) {
      console.error('VSLeads Service: Erro ao atualizar captura de dados:', error);
      throw error;
    }
  },

  /**
   * PATCH /password-capture/:id_video
   * Atualiza as configurações de captura de senha de um vídeo
   */
  updatePasswordCapture: async (videoId: string, data: Partial<VSLeadFormConfig>): Promise<any> => {
    try {
      const response = await api.patch(`/videos/password-capture/${videoId}`, data);
      return response;
    } catch (error) {
      console.error('VSLeads Service: Erro ao atualizar captura de senha:', error);
      throw error;
    }
  },

  /**
   * GET /capture/leads
   * Lista paginada dos leads de TODOS os vídeos do usuário. Filtro por vídeo,
   * data e busca + ordenação são resolvidos no backend.
   */
  getLeadsReport: async (params: LeadsReportParams): Promise<LeadsReportResult> => {
    try {
      const qs = new URLSearchParams();
      qs.set('page', String(params.page));
      qs.set('pageSize', String(params.pageSize));
      if (params.videoId) qs.set('videoId', params.videoId);
      if (params.dateFrom) qs.set('dateFrom', params.dateFrom);
      if (params.dateTo) qs.set('dateTo', params.dateTo);
      if (params.search) qs.set('search', params.search);
      if (params.sortField) qs.set('sortField', params.sortField);
      if (params.sortDir) qs.set('sortDir', params.sortDir);

      const response = await api.get(`/capture/leads?${qs.toString()}`);
      const r = response?.response ?? response?.data?.response ?? response;
      return {
        data: Array.isArray(r?.data) ? r.data : [],
        total: r?.total ?? 0,
        page: r?.page ?? params.page,
        pageSize: r?.pageSize ?? params.pageSize,
      };
    } catch (error) {
      console.error('VSLeads Service: Erro ao buscar relatório de leads:', error);
      return { data: [], total: 0, page: params.page, pageSize: params.pageSize };
    }
  },
};

export default vsleadsService;
